import { api } from './api';

export type HudHintKind =
  | 'decision'
  | 'coverage'
  | 'pacing'
  | 'contradiction'
  | 'followup'
  | 'build'
  | 'message'
  | 'other';

export type HudHintPriority = 'high' | 'normal' | 'low';
export type HudHintStatus = 'active' | 'accepted' | 'ignored' | 'confirmed' | 'resolved' | 'expired';

export interface HudHint {
  hintId: string;
  kind: HudHintKind | string;
  text: string;
  detail: string;
  priority: HudHintPriority;
  status: HudHintStatus;
  raisedAt: string;
  closedAt?: string;
  by?: string;
  userId?: string;
  adrId?: string;
  updatedAt: string;
}

export type HudMessageStatus = 'waiting' | 'yes' | 'no' | 'expired';

export interface HudMessage {
  messageId: string;
  text: string;
  detail?: string;
  sentBy: string;
  sentByName: string;
  createdAt: string;
  expiresAt: string;
  deliveredAt?: string;
  response: 'yes' | 'no' | null;
  answeredBy?: string;
  answeredAt?: string;
  status: HudMessageStatus;
}

export interface HudDevice {
  deviceId: string;
  serial: string;
  name: string;
  role: 'primary' | 'viewer' | string;
  slot: string;
  battery: number | null;
  charging: boolean;
  firmware: string;
  arms: { left: boolean; right: boolean };
  lastSyncAt: string;
  ownerUserId: string;
  ownerEmail: string;
}

export interface HudSettings {
  quiet: boolean;
  quietUntil?: string | null;
}

export interface HudSettingsInput {
  quiet: boolean;
  quietMinutes?: number;
}

function parseSettings(raw: any): HudSettings {
  return { quiet: !!raw?.quiet, quietUntil: raw?.quietUntil ?? null };
}

export function isQuietActive(settings: HudSettings | undefined, now = Date.now()): boolean {
  if (!settings?.quiet) return false;
  if (!settings.quietUntil) return true;
  const until = new Date(settings.quietUntil).getTime();
  return Number.isNaN(until) || until > now;
}

export interface HudState {
  hints: HudHint[];
  messages: HudMessage[];
  devices: HudDevice[];
  settings: HudSettings;
}

export interface SendHudMessageInput {
  text: string;
  detail?: string;
  expiresInMinutes?: number;
}

export const HUD_MESSAGE_MAX_TEXT = 200;
export const HUD_MESSAGE_MAX_DETAIL = 800;

export async function getHudState(sessionId: string): Promise<HudState> {
  const res = await api.get(`/api/sessions/${sessionId}/hud`);
  const data = res.data || {};
  return {
    hints: data.hints || [],
    messages: data.messages || [],
    devices: data.devices || [],
    settings: parseSettings(data.settings),
  };
}

export async function sendHudMessage(sessionId: string, input: SendHudMessageInput): Promise<HudMessage> {
  const res = await api.post(`/api/sessions/${sessionId}/hud/messages`, input);
  return res.data;
}

export async function setHudSettings(sessionId: string, settings: HudSettingsInput): Promise<HudSettings> {
  const res = await api.put(`/api/sessions/${sessionId}/hud/settings`, settings);
  return parseSettings(res.data?.settings ?? res.data);
}
